/**
 * AuthContext — in-memory staff session state (ODE-P01-T10).
 *
 * The raw session token exists only in memory: a page refresh returns to the
 * login screen (fail-closed; no token persistence, no XSS-exposed storage).
 * The server remains the authority — every guarded request carries the token
 * and the T08 guard re-validates it.
 *
 * Traces to: ODE-PRD-REQ-077, ODE-PRD-REQ-090.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { api, type StaffInfo } from '../api/client.js';

export interface AuthSession {
  readonly token: string;
  readonly staff: StaffInfo;
}

interface AuthContextValue {
  readonly session: AuthSession | null;
  readonly login: (staffId: string, credential: string) => Promise<void>;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  initialSession = null,
}: {
  readonly children: ReactNode;
  /** Test seam: seed a session without a login round-trip (mirrors the backend options-override pattern). */
  readonly initialSession?: AuthSession | null;
}): JSX.Element {
  const [session, setSession] = useState<AuthSession | null>(initialSession);

  const login = useCallback(async (staffId: string, credential: string) => {
    const result = await api.login(staffId, credential);
    setSession({ token: result.token, staff: result.staff });
  }, []);

  const logout = useCallback(() => {
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, login, logout }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}